// src/plugins/utils.js
import platform from "platform";
import {
  toPersianDigits,
  toEnglishDigits,
  getDeviceIcon,
  validatePhoneNumber,
  validatePin,
} from "../utils";

const UtilsPlugin = {
  install(app) {
    const devLog = (...args) => {
      if (process.env.NODE_ENV !== "development") return;

      try {
        fetch("/__devlog", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            time: new Date().toISOString(),
            logs: args,
          }),
        });
      } catch (e) {
        // silent
      }
    };

    devLog("✅ UtilsPlugin installed");

    /**
     * 🔄 ریلود کامل اپلیکیشن با مدیریت صحیح Service Worker
     */
    const reloadApp = async (waitingServiceWorker = null) => {
      console.log("🔄 reloadApp called with:", waitingServiceWorker);
      devLog("🔄 Starting app reload process...");

      if (!("serviceWorker" in navigator)) {
        console.log("⚠️ Service Worker not supported");
        devLog("⚠️ Service Worker not supported, doing simple reload");
        window.location.reload();
        return;
      }

      try {
        const registration = await navigator.serviceWorker.getRegistration();
        console.log("📋 Registration:", registration);

        if (!registration) {
          console.log("⚠️ No registration found");
          devLog("⚠️ No registration found, doing simple reload");
          window.location.reload();
          return;
        }

        const serviceWorkerToActivate = waitingServiceWorker || registration.waiting;
        console.log("🎯 Service worker to activate:", serviceWorkerToActivate);

        if (serviceWorkerToActivate) {
          console.log("✅ Found waiting service worker, proceeding with activation...");
          devLog("✅ Found waiting service worker, activating...");

          const controllerChangePromise = new Promise((resolve) => {
            let resolved = false;

            const handleControllerChange = () => {
              if (!resolved) {
                resolved = true;
                console.log("✅ Controller changed!");
                devLog("✅ Controller changed, reloading page");
                navigator.serviceWorker.removeEventListener(
                  "controllerchange",
                  handleControllerChange
                );
                resolve();
              }
            };

            navigator.serviceWorker.addEventListener(
              "controllerchange",
              handleControllerChange
            );

            setTimeout(() => {
              if (!resolved) {
                resolved = true;
                console.log("⏱️ Timeout reached, forcing reload");
                devLog("⏱️ Timeout reached, forcing reload");
                navigator.serviceWorker.removeEventListener(
                  "controllerchange",
                  handleControllerChange
                );
                resolve();
              }
            }, 2000);
          });

          console.log("📤 Sending SKIP_WAITING message to service worker");
          devLog("📤 Sending SKIP_WAITING message");
          serviceWorkerToActivate.postMessage({ type: "SKIP_WAITING" });

          console.log("⏳ Waiting for controller change...");
          await controllerChangePromise;

          console.log("🔄 Reloading page now!");

          // ✅ اضافه کردن یه کوئری پارامتر برای force reload کامل
          window.location.href = window.location.href.split('?')[0] + '?updated=' + Date.now();
        } else {
          console.log("⚠️ No waiting service worker found");
          devLog("⚠️ No waiting service worker, trying update and reload");
          await registration.update();
          window.location.reload(true); // hard reload
        }
      } catch (error) {
        console.error("❌ Error during reload process:", error);
        devLog("❌ Error during reload process:", error);
        window.location.reload(true); // hard reload
      }
    };


    /**
     * 🔎 استخراج مدل از User-Agent (Fallback)
     */
    const extractModelFromUA = (ua) => {
      const patterns = [
        /(?:SM-|GT-|SCH-|SGH-)([A-Z0-9]+)/i,
        /;\s*([A-Z]\d{0,4})\s*(?:Build|\))/i,
        /;\s*((?:Redmi|Mi|POCO)\s+[^;)]+)/i,
        /;\s*(M\d+[A-Z]*)\s*(?:Build|\))/i,
        /;\s*((?:ALP|STK|ELE|VOG|ANE|HW|NEO)-[A-Z0-9]+)/i,
        /;\s*((?:CPH|PBAM|PBCM)\d+)/i,
        /;\s*([VI]\d{4})/i,
        /;\s*(RMX\d+)/i,
        /;\s*((?:OnePlus|ONEPLUS)\s*[A-Z0-9\s]+)/i,
        /;\s*(Pixel\s*\d[^;)]*)/i,
      ];

      for (const pattern of patterns) {
        const match = ua.match(pattern);
        if (match && match[1]) {
          return match[1].trim();
        }
      }

      return null;
    };

    /**
     * 🔎 تشخیص برند از روی مدل (Fallback)
     */
    const getBrandFromModel = (model) => {
      if (!model || model === "Unknown") return null;

      const modelPatterns = {
        Samsung: /^(SM-|GT-|SCH-|SGH-|SPH-|SHV-|SHW-)/i,
        Xiaomi: /^(Mi |Redmi|POCO|M\d)/i,
        Huawei: /^(HW-|NEO-|STK-|ALP-|ELE-|VOG-|ANE-)/i,
        Oppo: /^(CPH|PBAM|PBCM)/i,
        Vivo: /^[VI]\d{4}$/i,
        Realme: /^RMX/i,
        OnePlus: /^(OnePlus|ONEPLUS)/i,
        Google: /^Pixel/i,
        Nokia: /^Nokia/i,
        Motorola: /^(Moto|XT\d{4})/i,
      };

      for (const [brand, regex] of Object.entries(modelPatterns)) {
        if (regex.test(model)) {
          return brand;
        }
      }

      return null;
    };

    /**
     * 🔍 تشخیص دقیق اطلاعات دستگاه با Platform.js
     */
    const getDeviceInfo = async () => {
      let clientHintsData = {};

      try {
        if (navigator.userAgentData) {
          const uaData = await navigator.userAgentData.getHighEntropyValues([
            "model",
            "platform",
            "platformVersion",
          ]);

          clientHintsData = {
            model: uaData.model,
            platform: uaData.platform,
            platformVersion: uaData.platformVersion,
            mobile: uaData.mobile,
          };
        }
      } catch (err) {
        devLog("⚠️ Client Hints not supported:", err.message);
      }

      const platformInfo = platform.parse(navigator.userAgent);

      let detectedModel = "Unknown";
      let detectedBrand = "Unknown";

      if (clientHintsData.model && clientHintsData.model !== "Unknown") {
        detectedModel = clientHintsData.model;
      } else if (platformInfo.product && platformInfo.product !== "Unknown") {
        detectedModel = platformInfo.product;
      } else {
        const manualModel = extractModelFromUA(navigator.userAgent);
        if (manualModel) {
          detectedModel = manualModel;
        }
      }

      if (platformInfo.manufacturer && platformInfo.manufacturer !== "Unknown") {
        detectedBrand = platformInfo.manufacturer;
      } else {
        const brandFromModel = getBrandFromModel(detectedModel);
        detectedBrand = brandFromModel || "Unknown";
      }

      devLog("🔍 Detection Results:", {
        detectedModel,
        detectedBrand,
        clientHints: clientHintsData,
        platformJS: {
          manufacturer: platformInfo.manufacturer,
          product: platformInfo.product,
          os: platformInfo.os,
        },
        ua: navigator.userAgent,
      });

      return {
        brand: detectedBrand,
        model: detectedModel,
        os: clientHintsData.platform || platformInfo.os?.family || "Unknown",
        osVersion:
          clientHintsData.platformVersion || platformInfo.os?.version || "Unknown",
        browser: platformInfo.name || "Unknown",
        browserVersion: platformInfo.version || "Unknown",
        mobile: clientHintsData.mobile ?? platformInfo.product !== null,
        fullUA: navigator.userAgent,
      };
    };

    /**
     * 📲 ساخت SMS URI براساس OS و Brand
     */
    const buildSmsUri = (phoneNumber, message, deviceInfo) => {
      const os = deviceInfo.os?.toLowerCase() || "";
      const brand = deviceInfo.brand?.toLowerCase() || "";
      const encodedMessage = encodeURIComponent(message);

      devLog("📲 Building SMS URI:", { os, brand, phoneNumber });

      if (os.includes("ios")) {
        return `sms:${phoneNumber}&body=${encodedMessage}`;
      }

      if (os.includes("android")) {
        if (brand === "xiaomi") {
          return `sms:${phoneNumber}?body=${encodedMessage}`;
        }
        return `sms:${phoneNumber}?body=${encodedMessage}`;
      }

      return `sms:${phoneNumber}?body=${encodedMessage}`;
    };

    /**
     * 📤 ارسال SMS
     */
    const sendSms = async (deviceId, phoneNumber, message) => {
      try {
        if (!validatePhoneNumber(phoneNumber)) {
          devLog("❌ Invalid phone number", phoneNumber);
          throw new Error("شماره تلفن معتبر نیست");
        }

        devLog("📨 Sending SMS", { deviceId, phoneNumber, message });

        const deviceInfo = await getDeviceInfo();
        const smsUri = buildSmsUri(phoneNumber, message, deviceInfo);

        devLog("✅ SMS URI generated:", smsUri);

        window.location.href = smsUri;

        return { success: true, smsUri };
      } catch (err) {
        devLog("❌ SMS send failed", err.message);
        return { success: false, error: err.message };
      }
    };

    const logSms = (deviceId, phoneNumber, message) => {
      try {
        const logKey = `smsLogs_${deviceId}`;
        const logs = JSON.parse(localStorage.getItem(logKey)) || [];

        logs.push({
          phoneNumber,
          message,
          timestamp: Date.now(),
        });

        localStorage.setItem(logKey, JSON.stringify(logs));
        devLog("📝 SMS logged", { deviceId, phoneNumber, message });
      } catch (err) {
        devLog("❌ Log SMS failed", err.message);
      }
    };

    const getSmsLogs = (deviceId) => {
      try {
        const logKey = `smsLogs_${deviceId}`;
        const logs = JSON.parse(localStorage.getItem(logKey)) || [];
        return logs.sort((a, b) => b.timestamp - a.timestamp);
      } catch (err) {
        devLog("❌ Read SMS logs failed", err.message);
        return [];
      }
    };

    app.config.globalProperties.$utils = {
      toPersianDigits,
      toEnglishDigits,
      getDeviceIcon,
      validatePhoneNumber,
      validatePin,
      reloadApp,
      sendSms,
      logSms,
      getSmsLogs,
      getDeviceInfo,
      devLog,
    };
  },
};

export default UtilsPlugin;
